import {Switch} from '@headlessui/react';
import React from 'react';
import {concatenate} from "services/string";

// Component ----

export type ToggleSwitchProps = {
    label?: string
    description?: string
    name?: string
    checked?: boolean
    disabled?: boolean
    short?: boolean
    multiline?: boolean
    onChange: (enabled: boolean) => any
}

function ToggleSwitch({description, checked, disabled, label, multiline, name, onChange, short}: ToggleSwitchProps) {
    return (
        <Switch.Group as="div" className="flex items-center">
            <Switch
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                className={concatenate(
                    short ? null : (disabled ? 'bg-gray-200 cursor-not-allowed' : checked ? 'bg-brand' : 'bg-gray-200'),
                    short ? 'h-5 w-10 group items-center' : 'h-6 w-11 border-2 border-transparent',
                    'relative inline-flex flex-shrink-0 rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand'
                )}
            >
                <span className="sr-only">{name || label}</span>
                {short ? <>
                    <span aria-hidden="true"
                          className="pointer-events-none absolute bg-white w-full h-full rounded-md"/>
                    <span
                        aria-hidden="true"
                        className={concatenate(
                          disabled ? 'bg-gray-200' : checked ? 'bg-brand' : 'bg-gray-200',
                            'pointer-events-none absolute h-4 w-9 mx-auto rounded-full transition-colors ease-in-out duration-200'
                        )}
                    />
                </> : null}
                <span
                    className={concatenate(
                        checked ? 'translate-x-5' : 'translate-x-0',
                        short ? 'border border-gray-200 absolute left-0' : 'relative',
                        'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    )}
                >
                <span
                    className={concatenate(
                        checked ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200',
                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity'
                    )}
                    aria-hidden="true"
                >
                    <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                        <path
                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <span
                    className={concatenate(
                        checked ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100',
                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity'
                    )}
                    aria-hidden="true"
                >
                        <svg className={concatenate("h-3 w-3", disabled ? 'text-gray-400' : 'text-brand' )} fill="currentColor" viewBox="0 0 12 12">
                            <path
                                d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"/>
                        </svg>
                    </span>
                </span>
            </Switch>

            {(label || description) &&
                <span className={concatenate(multiline && 'flex flex-grow flex-col', 'ml-3')}>
                    <Switch.Label as="span">
                        {label && <span className={concatenate(short? 'text-xs' : 'text-sm', 'font-medium text-gray-700')}>{label} </span>}
                        {description && !multiline && <span className="text-sm text-gray-500 ml-2">{description}</span>}
                    </Switch.Label>

                    {description && multiline &&
                        <Switch.Description as="span" className="text-sm text-gray-500 ml-2">
                            {description}
                        </Switch.Description>
                    }
                </span>
            }
        </Switch.Group>


    );
}

export default ToggleSwitch;